/**
 * Remap processed items from source year safeguard IDs to target year safeguard IDs.
 * Uses the source year's Combined data cross-references to find the mapping.
 *
 * When multiple source safeguards map to the same target, their scores are averaged.
 * Target safeguards with no source mapping simply won't appear in the output,
 * so the scoring pipeline treats them as unanswered (lowering the overall score).
 *
 * @param {Array} processedItems - Source project answers with maturityscore and policystatus
 * @param {Array} combinedData - Source year Combined array containing relatedCSItems
 * @param {string} targetYear - The target framework year (e.g. "2026")
 * @returns {Array} Remapped items with target year safeguard IDs
 */
function remapProcessedItemsForTargetYear(processedItems, combinedData, targetYear) {
	if (!processedItems || !combinedData || !targetYear) {
		console.warn('[remapProcessedItems] Missing required arguments');
		return processedItems || [];
	}

	const isCrfSStandard = (name) =>
		name === 'Cybersecurity Risk Foundation - Safeguards (CRF-S)' ||
		name === 'Cybersecurity Risk Foundation - Safeguards - Core Edition (CRF-S)';

	// Step 1: Build source→target ID mapping from Combined cross-references
	// { sourceId: Set([targetId1, targetId2, ...]) }
	const sourceToTargets = {};

	combinedData.forEach(entry => {
		const sourceId = entry.safeguardid && entry.safeguardid.S;
		if (!sourceId || !entry.relatedCSItems || !Array.isArray(entry.relatedCSItems)) return;

		entry.relatedCSItems.forEach(related => {
			if (!related.standardname || !isCrfSStandard(related.standardname.S)) return;
			if (!related.standardversion || related.standardversion.S !== targetYear) return;

			const uid = related.controluid && related.controluid.S;
			const uidMatchesYear = uid && (
				uid.startsWith('CRF-S-' + targetYear + '-') ||
				(uid.startsWith('CRF-S-') && uid.includes('-' + targetYear + '-'))
			);
			if (!uidMatchesYear) return;

			const targetId = related.controlid && related.controlid.S;
			if (!targetId) return;

			if (!sourceToTargets[sourceId]) sourceToTargets[sourceId] = new Set();
			sourceToTargets[sourceId].add(targetId);
		});
	});

	const mappedSourceCount = Object.keys(sourceToTargets).length;
	const totalTargetIds = Object.values(sourceToTargets).reduce((n, s) => n + s.size, 0);
	console.log('[remapProcessedItems] Mapping built:', {
		targetYear,
		sourceSafeguardsWithMapping: mappedSourceCount,
		totalTargetMappings: totalTargetIds
	});

	// Step 2: Build reverse mapping — target → [source items]
	// Also build a quick lookup of source answers by ID
	const sourceScores = {};
	processedItems.forEach(item => {
		sourceScores[item.safeguardid.S] = item;
	});

	// { targetId: [sourceItem, sourceItem, ...] }
	const targetToSourceItems = {};

	Object.keys(sourceToTargets).forEach(sourceId => {
		const sourceItem = sourceScores[sourceId];
		if (!sourceItem) return;

		sourceToTargets[sourceId].forEach(targetId => {
			if (!targetToSourceItems[targetId]) targetToSourceItems[targetId] = [];
			targetToSourceItems[targetId].push(sourceItem);
		});
	});

	// Step 3: Create one remapped item per target safeguard
	const remapped = [];

	Object.keys(targetToSourceItems).forEach(targetId => {
		const sources = targetToSourceItems[targetId];

		const naItems = sources.filter(i => parseInt(i.policystatus.N, 10) === 1);
		const scorableItems = sources.filter(i =>
			parseInt(i.policystatus.N, 10) !== 1 && i.maturityscore !== null
		);
		const unansweredItems = sources.filter(i =>
			parseInt(i.policystatus.N, 10) !== 1 && i.maturityscore === null
		);

		if (naItems.length === sources.length) {
			// All contributing sources are N/A
			remapped.push({
				safeguardid: { S: targetId },
				policystatus: { N: '1' },
				maturityscore: null
			});
		} else if (scorableItems.length > 0) {
			const avgScore = scorableItems.reduce((sum, i) => sum + i.maturityscore, 0) / scorableItems.length;
			remapped.push({
				safeguardid: { S: targetId },
				policystatus: { N: '0' },
				maturityscore: avgScore
			});
		} else {
			// Sources exist but none are scored (all unanswered but not N/A)
			remapped.push({
				safeguardid: { S: targetId },
				policystatus: { N: '0' },
				maturityscore: 0
			});
		}
	});

	console.log('[remapProcessedItems] Result:', {
		sourceItemCount: processedItems.length,
		remappedItemCount: remapped.length,
		targetSafeguardsWithData: remapped.filter(i => i.maturityscore !== null && i.maturityscore > 0).length,
		naItems: remapped.filter(i => parseInt(i.policystatus.N, 10) === 1).length
	});

	return remapped;
}

if (typeof module !== 'undefined' && module.exports) {
	module.exports = remapProcessedItemsForTargetYear;
} else if (typeof window !== 'undefined') {
	window.remapProcessedItemsForTargetYear = remapProcessedItemsForTargetYear;
}
